import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { modulesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  Plus,
  Server,
  Package,
  ShoppingCart,
  TrendingUp,
  Calculator,
  Bot,
  Calendar,
  Users,
  Truck,
  Gift,
  LayoutGrid,
  Edit,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  Server, Package, ShoppingCart, TrendingUp, Calculator,
  Bot, Calendar, Users, Truck, Gift, LayoutGrid
};

const ICON_OPTIONS = [
  'Server', 'Package', 'ShoppingCart', 'TrendingUp', 'Calculator',
  'Bot', 'Calendar', 'Users', 'Truck', 'Gift', 'LayoutGrid'
];

const STATUS_BADGES = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  in_development: { label: 'In Development', color: 'bg-yellow-100 text-yellow-700' },
  planned: { label: 'Planned', color: 'bg-blue-100 text-blue-700' },
  backlog: { label: 'Backlog', color: 'bg-gray-100 text-gray-700' },
};

const emptyModule = {
  name: '',
  code: '',
  short_description: '',
  full_description: '',
  icon: 'LayoutGrid',
  status: 'planned',
  price: 0,
  key_features: [],
  target_audience: '',
  value_proposition: '',
  pricing_model: 'monthly',
  discounts: '',
  requirements: '',
  api_endpoints: '',
  database_schema: '',
  integrations: '',
  github_link: '',
  launch_date: '',
};

export default function ModulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState(emptyModule);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await modulesAPI.getAll();
      setModules(res.data);
    } catch (err) {
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (module = null) => {
    if (module) {
      setEditingModule(module);
      setModuleForm({
        name: module.name || '',
        code: module.code || '',
        short_description: module.short_description || '',
        full_description: module.full_description || '',
        icon: module.icon || 'LayoutGrid',
        status: module.status || 'planned',
        price: module.price || 0,
        key_features: module.key_features || [],
        target_audience: module.target_audience || '',
        value_proposition: module.value_proposition || '',
        pricing_model: module.pricing_model || 'monthly',
        discounts: module.discounts || '',
        requirements: module.requirements || '',
        api_endpoints: module.api_endpoints || '',
        database_schema: module.database_schema || '',
        integrations: module.integrations || '',
        github_link: module.github_link || '',
        launch_date: module.launch_date || '',
      });
    } else {
      setEditingModule(null);
      setModuleForm(emptyModule);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!moduleForm.name) {
      toast.error('Module name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingModule) {
        await modulesAPI.update(editingModule.id, moduleForm);
        toast.success('Module updated');
      } else {
        await modulesAPI.create(moduleForm);
        toast.success('Module created');
      }
      setShowModal(false);
      fetchModules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (moduleId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    
    try {
      await modulesAPI.delete(moduleId);
      toast.success('Module deleted');
      fetchModules();
    } catch (err) {
      toast.error('Failed to delete module');
    }
  };

  const filteredModules = modules.filter(m => {
    if (searchQuery && !m.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterStatus && m.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout pageTitle="Modules">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Modules">
      <div className="space-y-6" data-testid="modules-page">
        {/* Top Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={16} />
              <Input
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-64"
                data-testid="search-modules"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white"
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="in_development">In Development</option>
              <option value="planned">Planned</option>
              <option value="backlog">Backlog</option>
            </select>
          </div>
          
          {user?.role === 'admin' && (
            <Button
              onClick={() => handleOpenModal()}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white flex-shrink-0"
              data-testid="add-module-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Module
            </Button>
          )}
        </div>

        {/* Module Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredModules.map(module => {
            const IconComponent = ICON_MAP[module.icon] || LayoutGrid;
            const statusBadge = STATUS_BADGES[module.status] || STATUS_BADGES.backlog;
            
            return (
              <div
                key={module.id}
                className="bg-white border border-[#E0E0E0] rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer relative group"
                onClick={() => navigate(`/modules/${module.id}`)}
                data-testid={`module-card-${module.id}`}
              >
                {/* Admin Actions */}
                {user?.role === 'admin' && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(module);
                      }}
                      className="p-1.5 bg-white border border-[#E0E0E0] rounded hover:bg-[#F5F5F5]"
                      data-testid={`edit-module-${module.id}`}
                    >
                      <Edit size={14} className="text-[#666666]" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(module.id, e)}
                      className="p-1.5 bg-white border border-[#E0E0E0] rounded hover:bg-red-50"
                      data-testid={`delete-module-${module.id}`}
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#CFFF40] flex items-center justify-center">
                    <IconComponent size={20} className="text-[#101010]" />
                  </div>
                  <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                </div>
                
                <h3 className="text-lg font-semibold text-[#111111] mb-1 flex items-center gap-2">
                  <span className="truncate">{module.name}</span>
                  {module.code && (
                    <span className="font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#101010] text-[#CFFF40] shrink-0" data-testid={`module-code-${module.id}`}>
                      {module.code}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-[#666666] mb-3 line-clamp-2">{module.short_description}</p>
                
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-[#666666]">
                    {module.price > 0 ? `$${module.price}/mo` : 'Included'}
                  </span>
                  <span className="text-[#666666]">
                    {module.restaurant_count} restaurants
                  </span>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#666666]">Progress</span>
                    <span className="text-[#111111] font-medium">{module.progress}%</span>
                  </div>
                  <Progress value={module.progress} className="h-2" />
                </div>
                
                {module.launch_date && (
                  <p className="text-xs text-[#666666] mt-3">
                    Launch: {new Date(module.launch_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {filteredModules.length === 0 && (
          <div className="text-center py-12">
            <LayoutGrid size={48} className="mx-auto text-[#E0E0E0] mb-4" />
            <p className="text-[#666666]">No modules found</p>
          </div>
        )}
      </div>

      {/* Module Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModule ? 'Edit Module' : 'Add Module'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Module Name *</Label>
                <Input
                  value={moduleForm.name}
                  onChange={(e) => setModuleForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., AI Booking System"
                />
              </div>
              <div>
                <Label>Icon</Label>
                <Select
                  value={moduleForm.icon}
                  onValueChange={(v) => setModuleForm(f => ({ ...f, icon: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(icon => {
                      const IconComp = ICON_MAP[icon];
                      return (
                        <SelectItem key={icon} value={icon}>
                          <div className="flex items-center gap-2">
                            <IconComp size={14} />
                            {icon}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Short Description</Label>
              <Input
                value={moduleForm.short_description}
                onChange={(e) => setModuleForm(f => ({ ...f, short_description: e.target.value }))}
                placeholder="Brief description"
              />
            </div>

            <div>
              <Label>Project Code</Label>
              <Input
                value={moduleForm.code}
                onChange={(e) => setModuleForm(f => ({ ...f, code: e.target.value }))}
                placeholder="e.g. ERP (auto-generated if left empty)"
                data-testid="module-code-input"
              />
              <p className="text-xs text-[#999999] mt-1">Used for task numbers, e.g. ERP-301</p>
            </div>
            
            <div>
              <Label>Full Description</Label>
              <Textarea
                value={moduleForm.full_description}
                onChange={(e) => setModuleForm(f => ({ ...f, full_description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={moduleForm.status}
                  onValueChange={(v) => setModuleForm(f => ({ ...f, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="in_development">In Development</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="backlog">Backlog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price ($/mo)</Label>
                <Input
                  type="number"
                  value={moduleForm.price}
                  onChange={(e) => setModuleForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Launch Date</Label>
                <Input
                  type="date"
                  value={moduleForm.launch_date}
                  onChange={(e) => setModuleForm(f => ({ ...f, launch_date: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Target Audience</Label>
              <Input
                value={moduleForm.target_audience}
                onChange={(e) => setModuleForm(f => ({ ...f, target_audience: e.target.value }))}
                placeholder="Who is this module for?"
              />
            </div>
            
            <div>
              <Label>Value Proposition</Label>
              <Textarea
                value={moduleForm.value_proposition}
                onChange={(e) => setModuleForm(f => ({ ...f, value_proposition: e.target.value }))}
                rows={2}
                placeholder="What value does this module provide?"
              />
            </div>
            
            <div>
              <Label>GitHub Link</Label>
              <Input
                value={moduleForm.github_link}
                onChange={(e) => setModuleForm(f => ({ ...f, github_link: e.target.value }))}
                placeholder="https://github.com/..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
            >
              {saving ? 'Saving...' : editingModule ? 'Update Module' : 'Create Module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
